import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useSchool } from "@/context/SchoolContext";
import { initials } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Loader2, Printer, Trophy, Layers, Settings2, Eye, Search, LayoutTemplate, Palette, Save, PanelsTopLeft, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { UseTemplateDialog } from "@/components/UseTemplateDialog";
import ProgressCardMode from "./ProgressCardMode";
import "./report-card.css";

const gradeColor = (g) => ({
  A1: "bg-emerald-100 text-emerald-700", A2: "bg-emerald-100 text-emerald-700",
  B1: "bg-blue-100 text-blue-700", B2: "bg-blue-100 text-blue-700",
  C1: "bg-amber-100 text-amber-700", C2: "bg-amber-100 text-amber-700",
  D: "bg-orange-100 text-orange-700", E: "bg-rose-100 text-rose-700",
}[g] || "bg-slate-100 text-slate-600");

const FIELD_TOGGLES = [
  ["logo", "School Logo"], ["school_name", "School Name"], ["tag_line", "Tag Line"],
  ["school_code", "School Code"], ["affiliated_to", "Affiliated To"], ["udise_code", "UDISE Code"],
  ["address", "Address"], ["school_number", "Phone"], ["marksheet_qr", "QR Code"], ["watermark_logo", "Watermark"],
];
const ALL_KEYS = FIELD_TOGGLES.map((k) => k[0]);

const SAMPLE_REPORT = {
  student: { id: "sample", name: "Krishna Yadav", class_name: "3rd", section: "A", roll_number: "5", admission_number: "1", father_name: "Ranjeet Singh", mother_name: "Sunita Devi" },
  subjects: [
    { subject_id: "1", subject_name: "English", max_marks: 100, pass_marks: 33, obtained: 82, percentage: 82, grade: "A2", status: "Pass", absent: false },
    { subject_id: "2", subject_name: "Mathematics", max_marks: 100, pass_marks: 33, obtained: 91, percentage: 91, grade: "A1", status: "Pass", absent: false },
    { subject_id: "3", subject_name: "Science", max_marks: 100, pass_marks: 33, obtained: 74, percentage: 74, grade: "B1", status: "Pass", absent: false },
  ],
  total_max: 300, total_obtained: 247, percentage: 82.3, overall_grade: "A2", result: "Pass", rank: 2, class_size: 35,
};
const SAMPLE_EXAM = { name: "Half Yearly Examination", term: "Term 1", class_name: "3rd" };

function ReportCardView({ report: r, exam, school, cumulative }) {
  const mf = school?.marksheet_fields;
  const show = (k) => !mf || mf.length === 0 || mf.includes(k);
  const rcs = school?.report_card_settings || {};
  return (
    <div className="report-card bg-white p-8 text-slate-800 relative overflow-hidden">
      {show("watermark_logo") && school?.watermark_logo && (
        <img src={school.watermark_logo} alt="" aria-hidden className="pointer-events-none select-none absolute inset-0 m-auto max-w-[55%] max-h-[55%] opacity-[0.06] object-contain" />
      )}
      <div className="relative text-center border-b-2 border-slate-800 pb-3 mb-4 flex items-center justify-center gap-3">
        {show("logo") && school?.logo && <img src={school.logo} alt="logo" className="w-14 h-14 object-contain shrink-0" />}
        <div className="min-w-0">
          {show("school_name") && <h2 className="text-2xl font-extrabold tracking-tight">{school?.name || "SSSPS"}</h2>}
          {show("tag_line") && school?.tag_line && <p className="text-xs text-slate-500 italic">{school.tag_line}</p>}
          <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap justify-center gap-x-3">
            {show("school_code") && school?.school_code && <span>Code: {school.school_code}</span>}
            {show("affiliated_to") && school?.affiliated_to && <span>Affiliated to: {school.affiliated_to}</span>}
            {show("udise_code") && school?.udise_code && <span>UDISE: {school.udise_code}</span>}
          </div>
          {show("address") && school?.address && <p className="text-[11px] text-slate-500">{school.address}</p>}
          {show("school_number") && school?.school_number && <p className="text-[11px] text-slate-500">Ph: {school.school_number}</p>}
          <p className="text-sm text-slate-500 mt-1">{exam.name}{exam.term ? ` — ${exam.term}` : ""}</p>
          {cumulative && <p className="text-xs text-indigo-500 font-medium">Cumulative across selected terms</p>}
        </div>
        {show("marksheet_qr") && school?.marksheet_qr && <img src={school.marksheet_qr} alt="QR" className="w-14 h-14 object-contain shrink-0" />}
      </div>

      <div className="relative grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4">
        <div><b>Name:</b> {r.student.name}</div>
        <div><b>Class:</b> {r.student.class_name} {r.student.section}</div>
        <div><b>Roll No:</b> {r.student.roll_number || "—"}</div>
        <div><b>Adm No:</b> {r.student.admission_number}</div>
        <div><b>Father:</b> {r.student.father_name || "—"}</div>
        <div><b>Mother:</b> {r.student.mother_name || "—"}</div>
      </div>

      <table className="w-full text-sm border border-slate-300 border-collapse mb-4">
        <thead className="bg-slate-100">
          <tr>
            {["Subject", "Max", "Pass", cumulative ? "Score" : "Obtained", "%", "Grade", "Result"].map((h) => (
              <th key={h} className="border border-slate-300 px-2 py-1.5 text-left">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {r.subjects.map((s) => (
            <tr key={s.subject_id}>
              <td className="border border-slate-300 px-2 py-1.5 font-medium">{s.subject_name}</td>
              <td className="border border-slate-300 px-2 py-1.5">{s.max_marks}</td>
              <td className="border border-slate-300 px-2 py-1.5">{s.pass_marks}</td>
              <td className="border border-slate-300 px-2 py-1.5">{s.status === "N/A" ? "—" : s.absent ? "AB" : s.obtained}</td>
              <td className="border border-slate-300 px-2 py-1.5">{s.status === "N/A" ? "—" : `${s.percentage}%`}</td>
              <td className="border border-slate-300 px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${gradeColor(s.grade)}`}>{s.grade}</span></td>
              <td className={`border border-slate-300 px-2 py-1.5 font-semibold ${s.status === "Fail" ? "text-rose-600" : s.status === "Pass" ? "text-emerald-600" : "text-slate-400"}`}>{s.status}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-slate-50 font-semibold">
            <td className="border border-slate-300 px-2 py-1.5">Total</td>
            <td className="border border-slate-300 px-2 py-1.5">{r.total_max}</td>
            <td className="border border-slate-300 px-2 py-1.5"></td>
            <td className="border border-slate-300 px-2 py-1.5">{r.total_obtained}</td>
            <td className="border border-slate-300 px-2 py-1.5" colSpan={3}>{r.percentage}%</td>
          </tr>
        </tfoot>
      </table>

      <div className="grid grid-cols-4 gap-3 text-center text-sm">
        <div className="border border-slate-200 rounded-lg py-2"><div className="text-slate-400 text-xs">Percentage</div><div className="text-lg font-bold">{r.percentage}%</div></div>
        <div className="border border-slate-200 rounded-lg py-2"><div className="text-slate-400 text-xs">Overall Grade</div><div className="text-lg font-bold">{r.overall_grade}</div></div>
        <div className="border border-slate-200 rounded-lg py-2"><div className="text-slate-400 text-xs">Result</div><div className={`text-lg font-bold ${r.result === "Fail" ? "text-rose-600" : "text-emerald-600"}`}>{r.result}</div></div>
        <div className="border border-slate-200 rounded-lg py-2"><div className="text-slate-400 text-xs">Class Rank</div><div className="text-lg font-bold">{r.rank} / {r.class_size}</div></div>
      </div>

      <div className="flex justify-between mt-10 text-sm text-slate-500">
        <div className="text-center"><div className="border-t border-slate-400 pt-1 px-6">{rcs.sig_left || "Class Teacher"}</div></div>
        <div className="text-center"><div className="border-t border-slate-400 pt-1 px-6">{rcs.sig_right || "Principal"}</div></div>
      </div>
    </div>
  );
}

export default function ReportCards() {
  const nav = useNavigate();
  const { school, refreshSchool } = useSchool();
  const [mode, setMode] = useState("single");
  const [exams, setExams] = useState([]);
  const [singleClass, setSingleClass] = useState("");
  const [examId, setExamId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [filterIds, setFilterIds] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [bindOpen, setBindOpen] = useState(false);

  const boundTpl = school?.report_card_settings?.template_id
    ? { id: school.report_card_settings.template_id, name: school.report_card_settings.template_name || "Canvas layout" }
    : null;

  // settings
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingCs, setSavingCs] = useState(false);
  const [fields, setFields] = useState(ALL_KEYS);
  const [sigLeft, setSigLeft] = useState("Class Teacher");
  const [sigRight, setSigRight] = useState("Principal");

  // consolidated
  const [conClass, setConClass] = useState("");
  const [conExamIds, setConExamIds] = useState([]);
  const [conMode, setConMode] = useState("average");

  useEffect(() => { api.get("/exams").then((r) => setExams(r.data)).catch(() => {}); }, []);

  const cumulative = mode === "consolidated";

  useEffect(() => {
    if (mode !== "single" || !examId) { if (mode === "single") setData(null); return; }
    setLoading(true);
    setSelected(new Set());
    api.get(`/exams/${examId}/report`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [examId, mode]);

  const classesFromExams = Array.from(new Map(exams.map((e) => [e.class_id, e.class_name])).entries());
  const conExams = exams.filter((e) => e.class_id === conClass);
  const toggleConExam = (id) => setConExamIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const generateConsolidated = async () => {
    if (conExamIds.length < 1) { toast.error("Select at least one exam"); return; }
    setLoading(true);
    setSelected(new Set());
    try {
      const r = await api.post("/reports/consolidated", { exam_ids: conExamIds, mode: conMode });
      setData(r.data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const switchMode = (m) => { setMode(m); setData(null); setExamId(""); setSingleClass(""); setConExamIds([]); setSelected(new Set()); };

  const reports = data?.reports || [];
  const filtered = reports.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [r.student.name, r.student.admission_number, r.student.roll_number, r.student.class_name].filter(Boolean).join(" ").toLowerCase().includes(q);
  }).slice().sort((a, b) => a.rank - b.rank);

  const toggleOne = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allChecked = filtered.length > 0 && filtered.every((r) => selected.has(r.student.id));
  const toggleAll = () => setSelected(() => allChecked ? new Set() : new Set(filtered.map((r) => r.student.id)));

  const pdfFromTemplate = async (ids) => {
    try {
      const r = await api.post(`/school/templates/${boundTpl.id}/pdf`, { student_ids: ids, exam_id: examId, origin: window.location.origin }, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error("Could not generate from the bound layout"); }
  };
  const openCard = (studentId) => {
    const report = reports.find((r) => r.student.id === studentId);
    if (report) setCard({ report, exam: data.exam, school: data.school });
  };
  const preview = (studentId) => openCard(studentId);
  const generate = (studentId) => {
    if (boundTpl && examId) { setBusyId(studentId); pdfFromTemplate([studentId]).finally(() => setBusyId(null)); return; }
    setBusyId(studentId);
    openCard(studentId);
    setTimeout(() => { document.body.classList.remove("printing-bulk"); window.print(); setBusyId(null); }, 400);
  };

  const printSingle = () => { document.body.classList.remove("printing-bulk"); window.print(); };
  const printList = (ids) => {
    if (boundTpl && examId) { pdfFromTemplate(ids || reports.map((r) => r.student.id)); return; }
    if (!reports.length) return;
    setFilterIds(ids);
    document.body.classList.add("printing-bulk");
    setTimeout(() => window.print(), 180);
    const cleanup = () => { document.body.classList.remove("printing-bulk"); setFilterIds(null); window.removeEventListener("afterprint", cleanup); };
    window.addEventListener("afterprint", cleanup);
    setTimeout(cleanup, 3000);
  };

  const bindTemplate = async (tpl) => {
    try {
      const rcs = { ...(school?.report_card_settings || {}), template_id: tpl.id, template_name: tpl.name };
      await api.put("/settings", { ...school, report_card_settings: rcs });
      await refreshSchool();
      toast.success(`Bound "${tpl.name}" as the printed layout`);
    } catch (e) { toast.error("Could not bind layout"); }
  };
  const unbindTemplate = async () => {
    try {
      const rcs = { ...(school?.report_card_settings || {}) }; delete rcs.template_id; delete rcs.template_name;
      await api.put("/settings", { ...school, report_card_settings: rcs });
      await refreshSchool();
      toast.success("Reverted to the default report card");
    } catch (e) { toast.error("Could not update"); }
  };

  const openSettings = () => {
    const mf = school?.marksheet_fields;
    setFields(mf && mf.length ? [...mf] : [...ALL_KEYS]);
    const rcs = school?.report_card_settings || {};
    setSigLeft(rcs.sig_left || "Class Teacher");
    setSigRight(rcs.sig_right || "Principal");
    setSettingsOpen(true);
  };
  const toggleField = (k) => setFields((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);
  const saveSettings = async () => {
    setSavingCs(true);
    try {
      const rcs = { ...(school?.report_card_settings || {}), sig_left: sigLeft, sig_right: sigRight };
      await api.put("/settings", { ...school, marksheet_fields: fields, report_card_settings: rcs });
      await refreshSchool();
      toast.success("Report card settings saved");
      setSettingsOpen(false);
    } catch (e) { toast.error("Could not save settings"); }
    finally { setSavingCs(false); }
  };

  const previewSchool = { ...(school || {}), marksheet_fields: fields, report_card_settings: { ...(school?.report_card_settings || {}), sig_left: sigLeft, sig_right: sigRight } };
  const previewReport = reports[0] || SAMPLE_REPORT;
  const previewExam = data?.exam || SAMPLE_EXAM;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><FileText className="text-indigo-500" size={26} />Report Cards</h1>
          <p className="text-slate-500">Generate printable report cards — single exam or consolidated across terms.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="gap-1.5" onClick={() => { if (!filtered.length) { toast.error("Select an exam with students first"); return; } setTplOpen(true); }} data-testid="rc-use-template-btn"><LayoutTemplate size={16} />Use Ready Template</Button>
          <Button variant="outline" className="gap-1.5" onClick={openSettings} data-testid="report-card-settings-btn"><Settings2 size={16} />Report Card Settings</Button>
          <Button className="gap-1.5 bg-gradient-to-r from-violet-600 to-indigo-600" onClick={() => nav("/templates/designer")} data-testid="report-card-canvas-btn"><Palette size={16} />Canvas</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap rounded-xl border border-slate-200 bg-white px-4 py-2.5" data-testid="rc-layout-bar">
        <PanelsTopLeft size={16} className="text-slate-400" />
        <span className="text-sm text-slate-500">Printed layout:</span>
        {boundTpl ? (
          <>
            <span className="text-sm font-semibold text-indigo-600" data-testid="rc-bound-name">{boundTpl.name}</span>
            <Button size="sm" variant="ghost" className="h-7 gap-1 text-slate-500" onClick={() => setBindOpen(true)} data-testid="rc-change-layout">Change</Button>
            <Button size="sm" variant="ghost" className="h-7 gap-1 text-rose-500" onClick={unbindTemplate} data-testid="rc-unbind-layout"><XIcon size={13} />Use default</Button>
          </>
        ) : (
          <>
            <span className="text-sm font-semibold text-slate-700">Default report card</span>
            <Button size="sm" variant="outline" className="h-7 gap-1.5" onClick={() => setBindOpen(true)} data-testid="rc-bind-layout"><LayoutTemplate size={14} />Use a Canvas layout</Button>
          </>
        )}
      </div>

      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
        <button onClick={() => switchMode("single")} className={`px-4 py-1.5 rounded-md text-sm font-medium ${mode === "single" ? "bg-indigo-600 text-white" : "text-slate-500"}`} data-testid="mode-single">Single Exam</button>
        <button onClick={() => switchMode("consolidated")} className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 ${mode === "consolidated" ? "bg-indigo-600 text-white" : "text-slate-500"}`} data-testid="mode-consolidated"><Layers size={14} />Consolidated</button>
        <button onClick={() => switchMode("progress")} className={`px-4 py-1.5 rounded-md text-sm font-medium ${mode === "progress" ? "bg-indigo-600 text-white" : "text-slate-500"}`} data-testid="mode-progress">Progress Card</button>
      </div>

      <div style={{ display: mode === "progress" ? "block" : "none" }}>
        <ProgressCardMode school={school} exams={exams} refreshSchool={refreshSchool} />
      </div>
      {mode === "single" ? (
        <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-3">
          <Select value={singleClass} onValueChange={(v) => { setSingleClass(v); setExamId(""); setData(null); }}>
            <SelectTrigger className="w-56" data-testid="report-class-select"><SelectValue placeholder="Select a class" /></SelectTrigger>
            <SelectContent>{classesFromExams.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={examId} onValueChange={setExamId}>
            <SelectTrigger className="w-72" data-testid="report-exam-select"><SelectValue placeholder="Select an exam" /></SelectTrigger>
            <SelectContent>{(singleClass ? exams.filter((e) => e.class_id === singleClass) : exams).map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.class_name} · {e.term}</SelectItem>)}</SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search name, admission no, roll…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="rc-search" />
          </div>
          <div className="ml-auto flex items-center gap-3">
            {selected.size > 0 && (
              <Button size="sm" className="gap-1.5" onClick={() => printList([...selected])} data-testid="rc-generate-selected"><Printer size={14} />Generate Selected ({selected.size})</Button>
            )}
            {data && reports.length > 0 && <Button size="sm" variant="outline" onClick={() => printList(null)} className="gap-1.5" data-testid="print-all-btn"><Printer size={14} />Print All ({reports.length})</Button>}
            {data && <span className="text-xs text-slate-400">{filtered.length} of {reports.length}</span>}
          </div>
        </div>
      ) : mode === "consolidated" ? (
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-slate-500 font-medium">Class</label>
              <Select value={conClass} onValueChange={(v) => { setConClass(v); setConExamIds([]); }}>
                <SelectTrigger className="w-56 mt-1" data-testid="con-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent>{classesFromExams.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-medium">Combine by</label>
              <Select value={conMode} onValueChange={setConMode}>
                <SelectTrigger className="w-44 mt-1" data-testid="con-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="average">Average per subject</SelectItem>
                  <SelectItem value="best">Best of per subject</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={generateConsolidated} disabled={!conExamIds.length} data-testid="con-generate-btn">Generate</Button>
          </div>
          {conClass ? (
            <div>
              <label className="text-xs text-slate-500 font-medium">Select exams to combine</label>
              <div className="mt-1 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {conExams.length === 0 && <p className="text-sm text-slate-400">No exams for this class yet.</p>}
                {conExams.map((e) => (
                  <label key={e.id} className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-50" data-testid={`con-exam-${e.id}`}>
                    <Checkbox checked={conExamIds.includes(e.id)} onCheckedChange={() => toggleConExam(e.id)} />
                    <span className="text-sm text-slate-700">{e.name} <span className="text-slate-400">· {e.term}</span></span>
                  </label>
                ))}
              </div>
            </div>
          ) : <p className="text-sm text-slate-400">Select a class to pick its exams.</p>}
        </div>
      ) : null}

      {mode !== "progress" && <div className="bg-white rounded-xl border border-slate-200 table-scroll">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          !data ? <div className="py-16 text-center text-slate-400">{mode === "single" ? "Choose an exam to see students." : "Pick exams and click Generate."}</div> :
          filtered.length === 0 ? <div className="py-16 text-center text-slate-400"><FileText size={40} className="mx-auto mb-2 opacity-40" />No students found.</div> : (
            <>
              <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-medium text-slate-600">{data.exam.name} · {data.exam.class_name}{data.source_exams ? ` · ${data.source_exams.join(" + ")}` : ""}</div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-3 w-10"><input type="checkbox" className="h-4 w-4 accent-indigo-600 cursor-pointer" checked={allChecked} onChange={toggleAll} data-testid="rc-select-all" /></th>
                    <th className="text-left px-4 py-3">Student</th>
                    <th className="text-left px-4 py-3">Adm No.</th>
                    <th className="text-left px-4 py-3">Rank</th>
                    <th className="text-left px-4 py-3">Score</th>
                    <th className="text-left px-4 py-3">%</th>
                    <th className="text-left px-4 py-3">Result</th>
                    <th className="text-right px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((r) => (
                    <tr key={r.student.id} className="hover:bg-slate-50" data-testid={`report-row-${r.student.admission_number}`}>
                      <td className="px-4 py-2.5"><input type="checkbox" className="h-4 w-4 accent-indigo-600 cursor-pointer" checked={selected.has(r.student.id)} onChange={() => toggleOne(r.student.id)} data-testid={`rc-select-${r.student.id}`} /></td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-semibold shrink-0">{initials(r.student.name)}</div>
                          <div>
                            <div className="font-medium text-slate-800">{r.student.name}</div>
                            <div className="text-xs text-slate-400">Roll {r.student.roll_number || "—"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-slate-500">{r.student.admission_number}</td>
                      <td className="px-4 py-2.5 font-semibold">{r.rank <= 3 ? <span className="inline-flex items-center gap-1 text-amber-600"><Trophy size={14} />{r.rank}</span> : r.rank}</td>
                      <td className="px-4 py-2.5 text-slate-600">{r.total_obtained} / {r.total_max}</td>
                      <td className="px-4 py-2.5">{r.percentage}%</td>
                      <td className={`px-4 py-2.5 font-semibold ${r.result === "Fail" ? "text-rose-600" : "text-emerald-600"}`}>{r.result}</td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-2">
                          <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => preview(r.student.id)} data-testid={`rc-preview-${r.student.admission_number}`}><Eye size={14} />Preview</Button>
                          <Button size="sm" variant="outline" className="gap-1.5" disabled={busyId === r.student.id} onClick={() => generate(r.student.id)} data-testid={`rc-generate-${r.student.admission_number}`}>
                            {busyId === r.student.id ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}Generate
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
      </div>}

      <Dialog open={!!card} onOpenChange={(o) => !o && setCard(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0">
          <div className="print-area">{card && <ReportCardView report={card.report} exam={card.exam} school={card.school} cumulative={cumulative} />}</div>
          <div className="flex justify-end gap-2 p-4 border-t border-slate-100 no-print">
            <Button variant="outline" onClick={() => setCard(null)}>Close</Button>
            <Button onClick={printSingle} className="gap-1.5" data-testid="print-card-btn"><Printer size={16} />Print</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="w-[90vw] max-w-[90vw] max-h-[90vh] overflow-y-auto" data-testid="report-card-settings-dialog">
          <DialogHeader><DialogTitle>Report Card Settings</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-5">
            <div className="space-y-4 min-w-0">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                <Label className="mb-2 block">Show on Report Card</Label>
                <div className="grid grid-cols-2 gap-2">
                  {FIELD_TOGGLES.map(([k, lbl]) => (
                    <label key={k} className="flex items-center justify-between gap-2 py-1.5 px-3 rounded-lg border border-slate-100 bg-white">
                      <span className="text-sm text-slate-700">{lbl}</span>
                      <Switch checked={fields.includes(k)} onCheckedChange={() => toggleField(k)} data-testid={`rc-toggle-${k}`} />
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Left Signatory</Label><Input value={sigLeft} onChange={(e) => setSigLeft(e.target.value)} data-testid="rc-sig-left" /></div>
                <div><Label>Right Signatory</Label><Input value={sigRight} onChange={(e) => setSigRight(e.target.value)} data-testid="rc-sig-right" /></div>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Preview</Label>
              <div className="bg-slate-100 rounded-lg p-4 overflow-auto" style={{ maxHeight: "70vh" }}>
                <div className="origin-top mx-auto" style={{ width: 620 }}>
                  <div className="shadow-lg"><ReportCardView report={previewReport} exam={previewExam} school={previewSchool} cumulative={cumulative} /></div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={saveSettings} disabled={savingCs} className="gap-1.5" data-testid="rc-save-btn">{savingCs ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {data && reports.length > 0 && (
        <div className="print-area-bulk">
          {reports.filter((r) => !filterIds || filterIds.includes(r.student.id)).map((r) => (
            <div key={r.student.id} className="report-card-page">
              <ReportCardView report={r} exam={data.exam} school={data.school} cumulative={cumulative} />
            </div>
          ))}
        </div>
      )}

      <UseTemplateDialog
        open={tplOpen}
        onOpenChange={setTplOpen}
        categories={["Marksheet", "Academic", "General"]}
        heading="Use a Ready Template — Report Card"
        getPayload={() => ({ exam_id: examId, student_ids: (selected.size ? filtered.filter((r) => selected.has(r.student.id)).map((r) => r.student.id) : filtered.map((r) => r.student.id)) })}
      />
      <UseTemplateDialog
        open={bindOpen}
        onOpenChange={setBindOpen}
        bindMode
        onPick={bindTemplate}
        categories={["Marksheet", "Academic", "General"]}
        heading="Choose a Canvas layout for Report Cards"
      />
    </div>
  );
}
